import React from 'react';
import { ToolPageSectionFields } from '../toolPageEditor/ToolPageSectionFields';
import RichTextField, { RichTextParagraphsField } from '../toolPageEditor/RichTextField';
import CustomTablesSectionFields from './CustomTablesSectionFields';
import OptionGroup from '../pageEditor/OptionGroup';
import { DragHandle, SortableItem, reorder } from '../pageEditor/sortable';

const GENERIC_TYPES = new Set(['description', 'video', 'faq', 'cta', 'steps', 'platformVideos']);

function ListField({ label, listId, items, onReorder, renderItem, onAdd, addLabel }) {
  return (
    <div className="tpb-field-group">
      <div className="tpb-field-group-head">
        <strong>{label}</strong>
        <button type="button" className="btn btn-secondary tpb-add-btn" onClick={onAdd}>
          <i className="fas fa-plus" aria-hidden="true" /> {addLabel}
        </button>
      </div>
      {items.map((item, idx) => (
        <SortableItem
          key={idx}
          listId={listId}
          index={idx}
          className="tpb-repeat-block"
          onMove={(from, to) => onReorder(reorder(items, from, to))}
        >
          <div className="tpb-nested-block-head">
            <DragHandle listId={listId} index={idx} />
            <strong>{label} {idx + 1}</strong>
          </div>
          {renderItem(item, idx)}
        </SortableItem>
      ))}
    </div>
  );
}

function patchAt(list, index, patch) {
  const next = [...list];
  next[index] = { ...next[index], ...patch };
  return next;
}

function SetupSectionFields({ section, onChange }) {
  const update = (patch) => onChange({ ...section, ...patch });
  const categories = section.setupCategories || [];
  const pcs = section.pcSpecs || [];
  const notes = section.streamingCommunication || [];

  return (
    <div className="admin-form">
      <OptionGroup title="Content">
        <div className="admin-grid-2">
          <label>
            Heading
            <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
          </label>
          <label>
            Highlight
            <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
          </label>
        </div>
        <label>
          Intro
          <textarea value={section.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={2} />
        </label>
      </OptionGroup>

      <OptionGroup title="Categories">
        {categories.map((category, catIdx) => (
          <SortableItem
            key={catIdx}
            listId="setup-categories"
            index={catIdx}
            className="tpb-nested-block"
            onMove={(from, to) => update({ setupCategories: reorder(categories, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="setup-categories" index={catIdx} />
              <strong>{category.name || `Category ${catIdx + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove category" onClick={() => update({ setupCategories: categories.filter((_, i) => i !== catIdx) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Emoji
                <input value={category.emoji || ''} onChange={(e) => update({ setupCategories: patchAt(categories, catIdx, { emoji: e.target.value }) })} />
              </label>
              <label>
                Name
                <input value={category.name || ''} onChange={(e) => update({ setupCategories: patchAt(categories, catIdx, { name: e.target.value }) })} />
              </label>
            </div>
            {(category.items || []).map((item, itemIdx) => (
              <SortableItem
                key={itemIdx}
                listId={`setup-cat-${catIdx}`}
                index={itemIdx}
                className="tpb-repeat-block"
                onMove={(from, to) => update({
                  setupCategories: patchAt(categories, catIdx, { items: reorder(category.items, from, to) }),
                })}
              >
                <div className="tpb-nested-block-head">
                  <DragHandle listId={`setup-cat-${catIdx}`} index={itemIdx} />
                  <strong>{item.label || `Item ${itemIdx + 1}`}</strong>
                  <button
                    type="button"
                    className="admin-icon-btn danger"
                    title="Remove item"
                    onClick={() => update({
                      setupCategories: patchAt(categories, catIdx, { items: category.items.filter((_, i) => i !== itemIdx) }),
                    })}
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
                <div className="admin-grid-2">
                  <label>
                    Label
                    <input
                      value={item.label || ''}
                      onChange={(e) => update({
                        setupCategories: patchAt(categories, catIdx, {
                          items: patchAt(category.items, itemIdx, { label: e.target.value }),
                        }),
                      })}
                    />
                  </label>
                  <label>
                    Link label
                    <input
                      value={item.link?.label || ''}
                      onChange={(e) => update({
                        setupCategories: patchAt(categories, catIdx, {
                          items: patchAt(category.items, itemIdx, {
                            link: e.target.value ? { href: item.link?.href || '', label: e.target.value } : undefined,
                          }),
                        }),
                      })}
                    />
                  </label>
                </div>
                <label>
                  Text
                  <textarea
                    value={item.text || ''}
                    rows={2}
                    onChange={(e) => update({
                      setupCategories: patchAt(categories, catIdx, {
                        items: patchAt(category.items, itemIdx, { text: e.target.value }),
                      }),
                    })}
                  />
                </label>
                <label>
                  Link URL
                  <input
                    value={item.link?.href || ''}
                    placeholder="https://"
                    onChange={(e) => update({
                      setupCategories: patchAt(categories, catIdx, {
                        items: patchAt(category.items, itemIdx, {
                          link: e.target.value ? { href: e.target.value, label: item.link?.label || 'Link' } : undefined,
                        }),
                      }),
                    })}
                  />
                </label>
              </SortableItem>
            ))}
            <button
              type="button"
              className="btn btn-secondary tpb-add-btn"
              onClick={() => update({
                setupCategories: patchAt(categories, catIdx, { items: [...(category.items || []), { label: '', text: '' }] }),
              })}
            >
              <i className="fas fa-plus" aria-hidden="true" /> Add item
            </button>
          </SortableItem>
        ))}
        <button
          type="button"
          className="btn btn-secondary tpb-add-btn"
          onClick={() => update({ setupCategories: [...categories, { emoji: '🎮', name: 'Category', items: [{ label: '', text: '' }] }] })}
        >
          <i className="fas fa-plus" aria-hidden="true" /> Add category
        </button>
      </OptionGroup>

      <OptionGroup title="PC specs" defaultOpen={false}>
        <label>
          Specs heading
          <input value={section.triplePcHeading || ''} onChange={(e) => update({ triplePcHeading: e.target.value })} />
        </label>
        {pcs.map((pc, pcIdx) => (
          <SortableItem
            key={pcIdx}
            listId="setup-pcs"
            index={pcIdx}
            className="tpb-nested-block"
            onMove={(from, to) => update({ pcSpecs: reorder(pcs, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="setup-pcs" index={pcIdx} />
              <strong>{pc.name || `PC ${pcIdx + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove PC" onClick={() => update({ pcSpecs: pcs.filter((_, i) => i !== pcIdx) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <div className="admin-grid-2">
              <label>
                Emoji
                <input value={pc.emoji || ''} onChange={(e) => update({ pcSpecs: patchAt(pcs, pcIdx, { emoji: e.target.value }) })} />
              </label>
              <label>
                Name
                <input value={pc.name || ''} onChange={(e) => update({ pcSpecs: patchAt(pcs, pcIdx, { name: e.target.value }) })} />
              </label>
            </div>
            {(pc.specs || []).map((spec, specIdx) => (
              <SortableItem
                key={specIdx}
                listId={`setup-pc-${pcIdx}`}
                index={specIdx}
                className="tpb-repeat-block"
                onMove={(from, to) => update({ pcSpecs: patchAt(pcs, pcIdx, { specs: reorder(pc.specs, from, to) }) })}
              >
                <div className="tpb-nested-block-head">
                  <DragHandle listId={`setup-pc-${pcIdx}`} index={specIdx} />
                  <strong>{spec.label || `Spec ${specIdx + 1}`}</strong>
                  <button
                    type="button"
                    className="admin-icon-btn danger"
                    title="Remove spec"
                    onClick={() => update({ pcSpecs: patchAt(pcs, pcIdx, { specs: pc.specs.filter((_, i) => i !== specIdx) }) })}
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
                <div className="admin-grid-2">
                  <label>
                    Label
                    <input value={spec.label || ''} onChange={(e) => update({ pcSpecs: patchAt(pcs, pcIdx, { specs: patchAt(pc.specs, specIdx, { label: e.target.value }) }) })} />
                  </label>
                  <label>
                    Value
                    <input value={spec.value || ''} onChange={(e) => update({ pcSpecs: patchAt(pcs, pcIdx, { specs: patchAt(pc.specs, specIdx, { value: e.target.value }) }) })} />
                  </label>
                </div>
              </SortableItem>
            ))}
            <button
              type="button"
              className="btn btn-secondary tpb-add-btn"
              onClick={() => update({ pcSpecs: patchAt(pcs, pcIdx, { specs: [...(pc.specs || []), { label: '', value: '' }] }) })}
            >
              <i className="fas fa-plus" aria-hidden="true" /> Add spec
            </button>
          </SortableItem>
        ))}
        <button
          type="button"
          className="btn btn-secondary tpb-add-btn"
          onClick={() => update({ pcSpecs: [...pcs, { emoji: '💻', name: 'PC', specs: [{ label: '', value: '' }] }] })}
        >
          <i className="fas fa-plus" aria-hidden="true" /> Add PC
        </button>
      </OptionGroup>

      <OptionGroup title="Communication" defaultOpen={false}>
        <label>
          Heading
          <input value={section.streamingCommunicationHeading || ''} onChange={(e) => update({ streamingCommunicationHeading: e.target.value })} />
        </label>
        {notes.map((note, noteIdx) => (
          <SortableItem
            key={noteIdx}
            listId="setup-notes"
            index={noteIdx}
            className="tpb-repeat-block"
            onMove={(from, to) => update({ streamingCommunication: reorder(notes, from, to) })}
          >
            <div className="tpb-nested-block-head">
              <DragHandle listId="setup-notes" index={noteIdx} />
              <strong>{note.label || `Block ${noteIdx + 1}`}</strong>
              <button type="button" className="admin-icon-btn danger" title="Remove block" onClick={() => update({ streamingCommunication: notes.filter((_, i) => i !== noteIdx) })}>
                <i className="fas fa-trash" aria-hidden="true" />
              </button>
            </div>
            <label>
              Label
              <input value={note.label || ''} onChange={(e) => update({ streamingCommunication: patchAt(notes, noteIdx, { label: e.target.value }) })} />
            </label>
            <label>
              Text
              <textarea value={note.text || ''} rows={3} onChange={(e) => update({ streamingCommunication: patchAt(notes, noteIdx, { text: e.target.value }) })} />
            </label>
          </SortableItem>
        ))}
        <button
          type="button"
          className="btn btn-secondary tpb-add-btn"
          onClick={() => update({ streamingCommunication: [...notes, { label: '', text: '' }] })}
        >
          <i className="fas fa-plus" aria-hidden="true" /> Add block
        </button>
      </OptionGroup>
    </div>
  );
}

export function StreamPageSectionFields({ section, onChange }) {
  if (GENERIC_TYPES.has(section.type)) {
    return <ToolPageSectionFields section={section} onChange={onChange} />;
  }

  const update = (patch) => onChange({ ...section, ...patch });

  switch (section.type) {
    case 'scheduleCalendar':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Heading
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <label>
            Intro
            <textarea value={section.intro || ''} onChange={(e) => update({ intro: e.target.value })} rows={3} />
          </label>
          <label>
            Footnote
            <textarea value={section.footnote || ''} onChange={(e) => update({ footnote: e.target.value })} rows={2} />
          </label>
          <label>
            TBC month note
            <textarea value={section.tbcNote || ''} onChange={(e) => update({ tbcNote: e.target.value })} rows={2} />
          </label>
          <label>
            Special Events note
            <textarea value={section.specialEventsNote || ''} onChange={(e) => update({ specialEventsNote: e.target.value })} rows={4} />
          </label>
          <p className="tpb-sidebar-hint">Game entries are managed in Admin → Schedule Games.</p>
        </div>
      );

    case 'aboutStream':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Heading
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <label>
            YouTube heading
            <input value={section.youtubeHeading || ''} onChange={(e) => update({ youtubeHeading: e.target.value })} />
          </label>
          <RichTextParagraphsField
            label="YouTube paragraphs"
            paragraphs={section.youtubeParagraphs || ['']}
            onChange={(youtubeParagraphs) => update({ youtubeParagraphs })}
          />
          <label>
            Twitch heading
            <input value={section.twitchHeading || ''} onChange={(e) => update({ twitchHeading: e.target.value })} />
          </label>
          <RichTextField label="Twitch paragraph" value={section.twitchParagraph || ''} onChange={(twitchParagraph) => update({ twitchParagraph })} />
          <label>
            Expect heading
            <input value={section.expectHeading || ''} onChange={(e) => update({ expectHeading: e.target.value })} />
          </label>
          <ListField
            label="Expect items"
            listId="expect-items"
            items={section.expectItems || []}
            addLabel="Add item"
            onReorder={(expectItems) => update({ expectItems })}
            onAdd={() => update({ expectItems: [...(section.expectItems || []), { label: '', text: '' }] })}
            renderItem={(item, idx) => (
              <>
                <label>
                  Label
                  <input
                    value={item.label || ''}
                    onChange={(e) => {
                      const expectItems = [...section.expectItems];
                      expectItems[idx] = { ...item, label: e.target.value };
                      update({ expectItems });
                    }}
                  />
                </label>
                <label>
                  Text
                  <textarea
                    value={item.text || ''}
                    rows={2}
                    onChange={(e) => {
                      const expectItems = [...section.expectItems];
                      expectItems[idx] = { ...item, text: e.target.value };
                      update({ expectItems });
                    }}
                  />
                </label>
                <div className="admin-grid-2">
                  <label>
                    Link path (optional)
                    <input
                      value={item.link || ''}
                      onChange={(e) => {
                        const expectItems = [...section.expectItems];
                        expectItems[idx] = { ...item, link: e.target.value };
                        update({ expectItems });
                      }}
                      placeholder="/shop"
                    />
                  </label>
                  <label>
                    Link label
                    <input
                      value={item.linkLabel || ''}
                      onChange={(e) => {
                        const expectItems = [...section.expectItems];
                        expectItems[idx] = { ...item, linkLabel: e.target.value };
                        update({ expectItems });
                      }}
                    />
                  </label>
                </div>
              </>
            )}
          />
          <label>
            Support text
            <textarea value={section.supportText || ''} onChange={(e) => update({ supportText: e.target.value })} rows={2} />
          </label>
        </div>
      );

    case 'features':
      return (
        <div className="admin-form">
          <div className="admin-grid-2">
            <label>
              Title before highlight
              <input value={section.heading || ''} onChange={(e) => update({ heading: e.target.value })} />
            </label>
            <label>
              Highlight
              <input value={section.highlight || ''} onChange={(e) => update({ highlight: e.target.value })} />
            </label>
          </div>
          <ListField
            label="Features"
            listId="features"
            items={section.features || []}
            addLabel="Add feature"
            onReorder={(features) => update({ features })}
            onAdd={() => update({
              features: [...(section.features || []), { icon: 'fas fa-star', iconColor: '#00eaff', title: '', description: '' }],
            })}
            renderItem={(feature, idx) => (
              <>
                <div className="admin-grid-2">
                  <label>
                    Icon class
                    <input
                      value={feature.icon || ''}
                      onChange={(e) => {
                        const features = [...section.features];
                        features[idx] = { ...feature, icon: e.target.value };
                        update({ features });
                      }}
                    />
                  </label>
                  <label>
                    Icon color
                    <input
                      value={feature.iconColor || ''}
                      onChange={(e) => {
                        const features = [...section.features];
                        features[idx] = { ...feature, iconColor: e.target.value };
                        update({ features });
                      }}
                    />
                  </label>
                </div>
                <label>
                  Title
                  <input
                    value={feature.title || ''}
                    onChange={(e) => {
                      const features = [...section.features];
                      features[idx] = { ...feature, title: e.target.value };
                      update({ features });
                    }}
                  />
                </label>
                <label>
                  Description
                  <textarea
                    value={feature.description || ''}
                    rows={2}
                    onChange={(e) => {
                      const features = [...section.features];
                      features[idx] = { ...feature, description: e.target.value };
                      update({ features });
                    }}
                  />
                </label>
              </>
            )}
          />
        </div>
      );

    case 'setup':
      return <SetupSectionFields section={section} onChange={onChange} />;

    case 'customTables':
      return <CustomTablesSectionFields section={section} onChange={onChange} />;

    default:
      return <p>Unknown section type.</p>;
  }
}
